import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const EMPTY = {
  name: "", working_days: [0,1,2,3,4],
  daily_minutes: 480, weekly_minutes: 2400,
  default_start: "09:00", default_end: "17:00", default_break_minutes: 60,
};

const minsToHM = (m) => `${Math.floor(m/60)}h${m%60 ? " " + (m%60) + "m" : ""}`;

export default function TemplatesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => setRows((await api.get("/pattern-templates")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEdit(t); setForm({...t}); setOpen(true); };

  const toggleDay = (d) => {
    const wd = form.working_days.includes(d) ? form.working_days.filter(x=>x!==d) : [...form.working_days, d].sort();
    setForm({...form, working_days: wd});
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = {...form, daily_minutes: Number(form.daily_minutes), weekly_minutes: Number(form.weekly_minutes), default_break_minutes: Number(form.default_break_minutes)};
      if (edit) await api.patch(`/pattern-templates/${edit.template_id}`, body);
      else await api.post("/pattern-templates", body);
      toast.success(edit ? "Template saved" : "Template created");
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/pattern-templates/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow">Configuration</div>
          <h1 className="font-display text-4xl mt-1">Working pattern templates</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-xl">Reusable shift presets assigned to employee profiles.</p>
        </div>
        <Button onClick={openCreate} data-testid="add-template-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
          <Plus className="h-4 w-4 mr-1"/>New template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map(t => (
          <div key={t.template_id} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">{minsToHM(t.daily_minutes)} / day · {minsToHM(t.weekly_minutes)} / week</div>
                <h3 className="font-display text-xl mt-1">{t.name}</h3>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={()=>openEdit(t)} data-testid={`tpl-edit-${t.template_id}`}><Pencil className="h-4 w-4"/></Button>
                <Button size="sm" variant="ghost" onClick={()=>del(t.template_id)} data-testid={`tpl-del-${t.template_id}`}><Trash2 className="h-4 w-4 text-slate-400"/></Button>
              </div>
            </div>
            <div className="mt-3 text-sm text-[#4B5773]">{t.default_start} → {t.default_end} <span className="text-slate-400">· {t.default_break_minutes}m break</span></div>
            <div className="mt-3 flex flex-wrap gap-1">
              {DAYS.map((d,i) => (
                <span key={d} className={`text-[11px] px-2 py-0.5 rounded-full ${t.working_days.includes(i) ? "bg-[#DBE7FE] text-[#1D4ED8]" : "bg-slate-100 text-slate-400"}`}>{d}</span>
              ))}
            </div>
          </div>
        ))}
        {!rows.length && <div className="text-sm text-[#4B5773]">No templates yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{edit ? "Edit template" : "New working pattern template"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="eyebrow">Name</Label>
              <Input required value={form.name} data-testid="tpl-name-input"
                onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Daily minutes</Label>
              <Input type="number" step="30" value={form.daily_minutes} data-testid="tpl-daily-input"
                onChange={(e)=>setForm({...form, daily_minutes: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Weekly minutes</Label>
              <Input type="number" step="30" value={form.weekly_minutes}
                onChange={(e)=>setForm({...form, weekly_minutes: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Start</Label>
              <Input type="time" value={form.default_start} step="1800"
                onChange={(e)=>setForm({...form, default_start: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">End</Label>
              <Input type="time" value={form.default_end} step="1800"
                onChange={(e)=>setForm({...form, default_end: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div className="col-span-2">
              <Label className="eyebrow">Break minutes</Label>
              <Input type="number" value={form.default_break_minutes} step="15"
                onChange={(e)=>setForm({...form, default_break_minutes: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div className="col-span-2">
              <Label className="eyebrow">Working days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DAYS.map((d,i) => (
                  <button type="button" key={d} onClick={()=>toggleDay(i)} data-testid={`tpl-wd-${d}`}
                    className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                      form.working_days.includes(i) ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                    }`}>{d}</button>
                ))}
              </div>
            </div>
            <DialogFooter className="col-span-2 mt-2">
              <Button type="submit" data-testid="tpl-submit-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">{edit ? "Save" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
